import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BootstrapContext } from "./bootstrapContext";
import { bootstrapQueryKey, fetchBootstrap } from "./bootstrapQuery";
import { useAuth } from "../auth/useAuth";
import { AUTH_STATES } from "../auth/authReducer";
import { ROLES } from "@/features/router/roles";

const STUDENT_REFETCH_INTERVAL_MS = 1000 * 60;

export function BootstrapProvider({ children }) {
  const queryClient = useQueryClient();
  const { status, user, photoURL } = useAuth();

  const userId = user?._id;

  const isAuthenticated = status === AUTH_STATES.AUTHENTICATED && !!userId;

  const query = useQuery({
    queryKey: bootstrapQueryKey(userId),
    queryFn: fetchBootstrap,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 2,
    retry: 1,

    refetchInterval: (query) => {
      const role = query.state.data?.role;
      if (role === ROLES.STUDENT) return STUDENT_REFETCH_INTERVAL_MS;
      return false;
    },
  });

  useEffect(() => {
    if (status === AUTH_STATES.UNAUTHENTICATED) {
      queryClient.removeQueries({
        queryKey: ["bootstrap"],
      });
    }
  }, [status, queryClient]);

  useEffect(() => {
    const handleAccessDisabled = () => {
      queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    };

    window.addEventListener("app:access-disabled", handleAccessDisabled);
    return () =>
      window.removeEventListener("app:access-disabled", handleAccessDisabled);
  }, [queryClient]);

  const value = {
    ...query,
    data: query.data ? { ...query.data, photoURL } : query.data,
  };

  return (
    <BootstrapContext.Provider value={value}>
      {children}
    </BootstrapContext.Provider>
  );
}
